import React, { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { map, toNumber } from "lodash";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import { IconButton } from "@/shared/ui/IconButton";
import { Column, Row } from "@/shared/ui/Blocks";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { SubmitableTextarea } from "@/shared/ui/SubmitableTextarea";
import { Person } from "@/features/people/graphql/generated";
import {
  ProjectNote,
  UpsertProjectNoteMutationVariables,
} from "@/features/projects/graphql/generated";
import { SubtitleText } from "@/shared/ui/Typography";
import { Skeleton } from "@/shared/ui/Skeleton";
import { LoadingSpinner } from "@/shared/ui/Loaders/Spinner";

dayjs.extend(utc);
dayjs.extend(timezone);

interface NoteProps {
  note: ProjectNote & { createdBy?: Person };
  isEditing: boolean;
  isDeleting: boolean;
  onEdit: (note: ProjectNote) => void;
  onDelete: (noteId: number) => void;
  onCancel: () => void;
  readOnly?: boolean;
}

const Note = React.memo(
  React.forwardRef<HTMLDivElement, NoteProps>(
    (
      { note, isEditing, isDeleting, onEdit, onDelete, onCancel, readOnly },
      ref
    ) => {
      const createdAt = dayjs(toNumber(note.created_at)).tz(dayjs.tz.guess());

      return (
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          <Row
            key={note.id}
            className="items-center gap-8 group border-b border-gray-200 pb-8 mb-4"
          >
            <Row className="items-center gap-4 relative">
              <AvatarWithInitials
                avatar={note.createdBy?.avatar || ""}
                initials={note.createdBy?.initials || ""}
                isContact={!note.createdBy?.id}
                name={note.createdBy?.name || "Private Note"}
              />
              <Column>
                <SubtitleText className="text-xs">
                  {createdAt.format("hh:mm")}
                </SubtitleText>
                <SubtitleText className="text-xs">
                  {createdAt.format("DD/MM/YYYY")}
                </SubtitleText>
              </Column>
            </Row>
            {isEditing && !readOnly ? (
              <Column className="flex-1">
                <SubmitableTextarea
                  defaultValue={note.text || ""}
                  onCancel={onCancel}
                  onSubmit={(text) => {
                    onEdit({ ...note, text });
                  }}
                />
              </Column>
            ) : (
              <>
                <Column className="flex-1">
                  <p>{note.text}</p>
                </Column>
                {!readOnly && (
                  <Column className="w-24">
                    {isDeleting ? (
                      <Row className="self-center">
                        <LoadingSpinner />
                      </Row>
                    ) : (
                      <Row className="self-center hidden group-hover:flex">
                        <IconButton
                          icon="Edit"
                          size="sm"
                          variant="ghost"
                          color="black"
                          onClick={() => onEdit(note)}
                        />
                        <IconButton
                          icon="Close"
                          size="sm"
                          variant="ghost"
                          color="black"
                          onClick={() => onDelete(note.id!)}
                        />
                      </Row>
                    )}
                  </Column>
                )}
              </>
            )}
          </Row>
        </motion.div>
      );
    }
  )
);

Note.displayName = "Note";

interface NotesSectionProps extends React.HTMLProps<HTMLDivElement> {
  notes: (ProjectNote & {
    createdBy?: Person;
    readOnly?: boolean;
  })[];
  onDeleteNote?: (noteId: number) => void;
  onCreateNote?: ({ text, type }: { text: string; type: string }) => void;
  onEditNote?: (note: UpsertProjectNoteMutationVariables) => void;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  readOnly?: boolean;
}

function NotesSection(props: NotesSectionProps) {
  const {
    title,
    notes,
    loading,
    onEditNote,
    onDeleteNote,
    onCreateNote,
    readOnly,
  } = props;

  const [isCreatingNote, setIsCreatingNote] = React.useState(false);
  const [editingNoteId, setEditingNoteId] = React.useState<number | null>(null);
  const [deletingNoteId, setDeletingNoteId] = React.useState<number | null>(
    null
  );

  const handleCreateNote = useCallback(
    (text: string) => {
      setIsCreatingNote(false);
      onCreateNote?.({
        text,
        type: "note",
      });
    },
    [onCreateNote]
  );

  const handleCancelNote = useCallback(() => {
    setIsCreatingNote(false);
    setEditingNoteId(null);
  }, []);

  const handleEditNote = useCallback(
    (note: ProjectNote) => {
      if (editingNoteId === note.id) {
        onEditNote?.(note as UpsertProjectNoteMutationVariables);
        setEditingNoteId(null);
      } else {
        setEditingNoteId(note.id || null);
      }
    },
    [editingNoteId, onEditNote]
  );

  const handleDeleteNote = useCallback(
    (noteId: number) => {
      setDeletingNoteId(noteId);
      onDeleteNote?.(noteId);
    },
    [onDeleteNote]
  );

  return (
    <section>
      <SectionHeader
        title={title || "Notes"}
        subtitle="Additional notes and comments about the candidate"
        RightComponent={
          readOnly ? null : (
            <IconButton
              onClick={() => setIsCreatingNote(true)}
              icon="Plus"
              label="Add Note"
              variant="secondary"
              size="md"
              color="blue"
            />
          )
        }
      />
      <Column className="w-full gap-4 mb-4">
        <AnimatePresence mode="popLayout">
          {map(notes, (note) => (
            <Note
              key={note.id}
              note={note}
              isEditing={editingNoteId === note.id}
              isDeleting={deletingNoteId === note.id}
              onEdit={handleEditNote}
              onDelete={handleDeleteNote}
              onCancel={handleCancelNote}
              readOnly={note?.readOnly || readOnly}
            />
          ))}
        </AnimatePresence>
        {loading && (
          <Skeleton className="w-full flex items-center gap-4 bg-transparent">
            <Skeleton className="h-10 w-10 rounded-full" />
            <Column className="gap-2">
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-3 w-10" />
            </Column>
            <Column className="flex-1 gap-2">
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </Column>
          </Skeleton>
        )}
      </Column>
      <AnimatePresence>
        {isCreatingNote && !readOnly && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <SubmitableTextarea
              onCancel={handleCancelNote}
              onSubmit={handleCreateNote}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export { NotesSection };
